import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax'
import _ from 'lodash'
import moment from 'moment'
import {
  CladeCountry,
  CladeDataT,
  CladeItem,
  DataT,
  FeaturePropertiesT,
  FeatureT,
  FormattedDataT,
  OldestCladeItem,
  OldestCladesByCountry,
} from './types'

dayjs.extend(minMax)

const getNewestClade = (items: CladeItem[]): OldestCladeItem[] => {
  const newestClade = Object.values(
    items.reduce((acc: Record<string, OldestCladeItem>, item) => {
      const clade = item.clade
      const date = item.date || item.dateSubmitted
      if (!clade || !date) return acc
      if (!acc[clade] || new Date(date) < new Date(acc[clade].date)) {
        acc[clade] = {
          ...item,
          clade,
          date,
          newDate: date,
          strain: item.strain,
        }
      }
      if (!acc[clade] || new Date(date) > new Date(acc[clade].newDate)) {
        acc[clade] = {
          ...item,
          date: acc[clade].date,
          clade: item.clade,
          strain: item.strain,
          newDate: date,
        }
      }
      return acc
    }, {})
  )
  return newestClade
}

const oldestCladeItemsByCountry = (
  cladeCountries: CladeCountry
): OldestCladesByCountry => {
  const clades = Object.fromEntries(
    Object.entries(cladeCountries)
      .filter(([country]) => country && country !== null) // Exclude invalid country keys
      .map(([country, items]) => {
        const filteredItems = items
          .map((item) => ({
            ...item,
            clade: item.clade,
            date: item.date,
          }))
          .filter(({ clade, date }) => !!clade && !!date) // Type guard for valid items
        return [country, getNewestClade(filteredItems)]
      })
      .filter(([country, oldestItems]) => !!country && oldestItems.length > 0) // Exclude countries with no valid clades
  )
  return clades
}

function assignCladeToCountryMap(
  countryPoly: { features: Feature[] },
  oldestClades: OldestCladesByCountry,
  countryFilter?: string
) {
  const countryWithClade = countryPoly.features.map((item) => {
    const id = countryFilter ? item.name : item.id

    const allClades = oldestClades[id]

    const newestClade = allClades?.reduce((newest, item) => {
      if (item.clade === 'Clade I' && allClades?.length > 1) {
        return newest // Skip "Clade I" if there are other clades available
      }
      return new Date(item.newDate) > new Date(newest.newDate) ? item : newest
    })

    return {
      ...item,
      properties: {
        ...item.properties,
        ...newestClade,
        clade: newestClade?.clade,
        color: !newestClade?.clade ? 'transparent' : 'purple',
      },
    }
  })
  return countryWithClade
}

function getCountryOldestClade(
  clade: CladeDataT,
  countryPoly: { features: Feature[] },
  dateFilter: string,
  countryFilter?: string
) {
  const cladeByAreas = _.groupBy(clade, (item) =>
    countryFilter ? item.area : item.areaAlpha3
  )

  const oldestClades = oldestCladeItemsByCountry(cladeByAreas)

  const filteredClades = Object.keys(oldestClades).reduce(
    (acc, country: string) => {
      const date = oldestClades[country][0].date
      if (moment(date).isSameOrBefore(moment(dateFilter))) {
        return {
          ...acc,
          [country]: oldestClades[country],
        }
      }
      return acc
    },
    {}
  )

  const countryWithClade = assignCladeToCountryMap(
    countryPoly,
    filteredClades,
    countryFilter
  )

  return countryWithClade
}

interface Feature {
  type: 'Feature'
  id: string
  name: string
  properties: {
    name: string
    coords?: any
    [key: string]: any
  }
  geometry: {
    type: string
    coordinates: number[][]
  }
}

export function getMpoxCollections(
  data: DataT[],
  clade: CladeDataT,
  countryPoly: { features: Feature[] },
  dateFilter: string,
  countryFilter?: string
): Record<string, any> {
  const filteredData = countryFilter
    ? data.filter((item) => item.areaAlpha3 === countryFilter)
    : data

  const countryClades = getCountryOldestClade(
    clade,
    countryPoly,
    dateFilter,
    countryFilter
  )

  const byLocation = _.groupBy(filteredData, (item) =>
    countryFilter
      ? `${item.areaLatitude}-${item.areaLongitude}`
      : `${item.areaAlpha3}`
  )

  function getCasesByLocation(location: string) {
    const cases = byLocation[location].reduce(
      (acc, item) => {
        const newAcc = { ...acc }
        const { isSuspected } = item
        if (typeof isSuspected === 'boolean' && !isSuspected) {
          newAcc.confirmed = [...newAcc.confirmed, item]
        } else if (isSuspected) {
          newAcc.suspected = [...newAcc.suspected, item]
        } else {
          newAcc.notSpecified = [...newAcc.notSpecified, item]
        }
        return newAcc
      },
      {
        confirmed: [] as any[],
        suspected: [] as any[],
        notSpecified: [] as any[],
      }
    )
    return cases
  }

  function calculateTotal(cases: any[], key = 'value') {
    return cases.reduce((total, item) => total + Number(item[key] || 0), 0)
  }

  function buildFeature(cases: DataT[], total: number, useCoords = false) {
    const defaultCase = cases[0]
    const { area, areaAlpha3: country, areaLatitude, areaLongitude } = cases[0]
    const countryInfo = countryPoly.features.find(
      (feature) => feature.id === defaultCase.areaAlpha3
    )?.properties
    const { coords, name } = countryInfo || {}

    const isAreaCountry = useCoords && name === area

    const nAreas = Object.keys(byLocation).length

    if (isAreaCountry && nAreas > 1) {
      return null
    }

    const lat = (useCoords ? areaLatitude : coords?.[1]) || areaLatitude
    const long = (useCoords ? areaLongitude : coords?.[0]) || areaLongitude

    return getFeatureStruct({
      area,
      country,
      lat,
      long,
      value: total,
      allCases: cases,
    })
  }

  const formattedData = Object.keys(byLocation).reduce(
    (acc, location) => {
      const cases = getCasesByLocation(location)

      const totalConfirmed = calculateTotal(cases.confirmed)
      const totalSuspected = calculateTotal(cases.suspected)
      const totalNotSpecified = calculateTotal(cases.notSpecified)

      const notSpecified = [...acc.notSpecified]
      if (totalNotSpecified > 0 && cases.notSpecified) {
        const newFeature = buildFeature(
          cases.notSpecified,
          totalNotSpecified,
          !!countryFilter
        )
        newFeature && notSpecified.push(newFeature)
      }

      const suspected = [...acc.suspected]
      if (totalSuspected > 0 && cases.suspected) {
        const newFeature = buildFeature(
          cases.suspected,
          totalSuspected,
          !!countryFilter
        )
        newFeature && suspected.push(newFeature)
      }

      const confirmed = [...acc.confirmed]
      if (totalConfirmed > 0 && cases.confirmed) {
        const newFeature = buildFeature(
          cases.confirmed,
          totalConfirmed,
          !!countryFilter
        )
        newFeature && confirmed.push(newFeature)
      }

      return {
        confirmed: confirmed,
        suspected: suspected,
        notSpecified: notSpecified,
      }
    },
    { confirmed: [], suspected: [], notSpecified: [] } as FormattedDataT
  )

  const type = { type: 'FeatureCollection' }

  return {
    confirmed: { ...type, features: formattedData.confirmed },
    suspected: { ...type, features: formattedData.suspected },
    notSpecified: { ...type, features: formattedData.notSpecified },
    clade: { ...type, features: countryClades as any },
  }
}

export interface CategoryConfig<T> {
  key: string
  label?: string
  predicate: (item: T) => boolean
}

export interface DynamicFeatureOptions<T> {
  valueKey?: keyof T | ((item: T) => number)
  categories: CategoryConfig<T>[]
  locationKey?: (item: T, countryFilter?: string) => string
}

export const suspectedConfig: DynamicFeatureOptions<DataT> = {
  categories: [
    {
      key: 'confirmed',
      predicate: (d) => typeof d.isSuspected === 'boolean' && !d.isSuspected,
    },
    {
      key: 'suspected',
      predicate: (d) => d.isSuspected === true,
    },
    {
      key: 'notSpecified',
      predicate: (d) => d.isSuspected === undefined || d.isSuspected === null,
    },
  ],
  valueKey: 'value', // Change if your metric lives under a different column
}

export function getBotulismFeatures(
  data: DataT[],
  clade: CladeDataT,
  countryPoly: { features: Feature[] },
  dateFilter: string,
  countryFilter: string | undefined,
  options = suspectedConfig
): Record<string, any> {
  // TODO: we are using absolute values for teh cases numbers since there are some negative values: eg: Foodborne Botulism (BoNT/AB)
  const valueAccessor =
    typeof options.valueKey === 'function'
      ? options.valueKey
      : (item: DataT) => {
          return typeof options.valueKey === 'string'
            ? Math.abs(Number(item[options.valueKey as keyof DataT] ?? 0))
            : options.valueKey
              ? options.valueKey(item)
              : 0
        }

  const locationAccessor =
    options.locationKey ??
    ((item: DataT, countryFilter?: string) =>
      countryFilter
        ? `${item.areaLatitude}-${item.areaLongitude}`
        : `${item.areaAlpha3}`)

  const initialCategoryState = options.categories.reduce<
    Record<string, DataT[]>
  >((acc, c) => ({ ...acc, [c.key]: [] }), {})

  function splitCases(cases: DataT[]): Record<string, DataT[]> {
    return cases.reduce<Record<string, DataT[]>>(
      (catAcc, row) => {
        const match = options.categories.find((c) => c.predicate(row))
        const key = match?.key ?? '__unmatched__'
        catAcc[key] = [...(catAcc[key] ?? []), row]
        return catAcc
      },
      { ...initialCategoryState }
    )
  }

  function calculateTotal(cases: DataT[]): number {
    return cases.reduce((total, item) => total + valueAccessor(item), 0)
  }

  function buildFeature(cases: DataT[], total: number, useCoords = false) {
    if (!cases.length) return null
    const base = cases[0]
    const { area, areaAlpha3: country, areaLatitude, areaLongitude } = base

    const countryInfo = countryPoly.features.find(
      (f) => f.id === base.areaAlpha3
    )?.properties
    const { coords, name } = countryInfo || {}

    const isAreaCountry = useCoords && name === area
    const nAreas = Object.keys(byLocation).length
    if (isAreaCountry && nAreas > 1) return null

    const lat = (useCoords ? areaLatitude : coords?.[1]) || areaLatitude
    const long = (useCoords ? areaLongitude : coords?.[0]) || areaLongitude

    return getFeatureStruct({
      area,
      country,
      lat,
      long,
      value: total,
      allCases: cases,
    })
  }

  const filteredData = countryFilter
    ? data.filter((d) => d.areaAlpha3 === countryFilter)
    : data

  const byLocation = _.groupBy(filteredData, (d) =>
    locationAccessor(d, countryFilter)
  )

  const featureCollections: Record<string, any[]> = options.categories.reduce(
    (acc, c) => ({ ...acc, [c.key]: [] }),
    {}
  )
  Object.keys(byLocation).forEach((locKey) => {
    const catCases = splitCases(byLocation[locKey])
    options.categories.forEach((cat) => {
      const cases = catCases[cat.key] || []
      const total = calculateTotal(cases)
      if (total <= 0) return
      const feature = buildFeature(cases, total, !!countryFilter)
      if (feature) featureCollections[cat.key].push(feature)
    })
  })

  const type = { type: 'FeatureCollection' as const }

  const result: Record<string, any> = {}
  for (const cat of options.categories) {
    const features = featureCollections[cat.key] as any
    if (!!features.length) {
      result[cat.key] = {
        ...type,
        features: features,
      }
    }
  }

  const countryClades = getCountryOldestClade(
    clade,
    countryPoly,
    dateFilter,
    countryFilter
  )

  return { ...result, countries: { ...type, features: countryClades as any } }
}

function getFeatureStruct(feature: FeaturePropertiesT): FeatureT {
  const { long, lat } = feature
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [long, lat],
    },
    properties: feature,
  }
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

export function getDates(dates: (dayjs.Dayjs | null)[] | (dayjs.Dayjs | null)) {
  if (!Array.isArray(dates)) {
    return dayjs(dates)?.format('YYYY-MM-DD')
  }
  const _dates = dates.filter(notEmpty)
  const elements = _dates.length
  if (elements === 0) return 'Not Reported'
  const oldestDate = dayjs.min(_dates)?.format('YYYY-MM-DD')
  const newestDate = dayjs.max(_dates)?.format('YYYY-MM-DD')

  if (oldestDate === newestDate) return newestDate

  return `${oldestDate} to ${newestDate}`
}
